import { UTCDate } from "@date-fns/utc";
import { endOfWeek, startOfWeek } from "date-fns";

export function formatDate(date: string | Date): string {
  const formatter = Intl.DateTimeFormat("da-DK");
  const d = new Date(date);
  return formatter.format(d);
}

// TODO : Should this replace the one above, or de we really need 2 similar formats???
export function formatDate2(date: string | Date): string {
  const formatter = Intl.DateTimeFormat("da-DK", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
  const d = new Date(date);
  return formatter.format(d);
}

export const { format: formatDateAndMonth } = Intl.DateTimeFormat("da-DK", {
  day: "2-digit",
  month: "2-digit",
});

export function formatDateTime(
  date: string | Date,
  formatOptions: Partial<Intl.DateTimeFormatOptions> = {
    hour: "numeric",
    minute: "numeric",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  },
): string {
  const formatter = Intl.DateTimeFormat("da-DK", formatOptions);
  const d = new Date(date);
  return formatter.format(d);
}

export function formatSince(
  startDate: string | Date,
  endDate: string | Date = new Date(),
): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Calculate difference in milliseconds
  const diffMs = end.getTime() - start.getTime();

  // Convert to days
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return "i fremtiden"; // "in the future"
  } else if (diffDays === 0) {
    return "i dag"; // "today"
  } else {
    return formatDays(diffDays); // "X days/years"
  }
}

// Rounds up to the nearest year if the number of days is greater than 364.
export function formatDays(days: number) {
  if (days > 364) {
    return `${Math.round(days / 365)} år`;
  }

  return days === 1 ? `${days} dag` : `${days} dage`;
}

export const DAYS = [
  { id: "monday", name: "Mandag", isWeekday: true, index: 0 },
  { id: "tuesday", name: "Tirsdag", isWeekday: true, index: 1 },
  { id: "wednesday", name: "Onsdag", isWeekday: true, index: 2 },
  { id: "thursday", name: "Torsdag", isWeekday: true, index: 3 },
  { id: "friday", name: "Fredag", isWeekday: true, index: 4 },
  { id: "saturday", name: "Lørdag", isWeekday: false, index: 5 },
  { id: "sunday", name: "Søndag", isWeekday: false, index: 6 },
] as const;

export const daysById = new Map<string, (typeof DAYS)[number]>(
  DAYS.map((day) => [day.id, day]),
);

export const WEEKDAYS = [
  { id: "monday", name: "Mandag" },
  { id: "tuesday", name: "Tirsdag" },
  { id: "wednesday", name: "Onsdag" },
  { id: "thursday", name: "Torsdag" },
  { id: "friday", name: "Fredag" },
] as const;

export type WeekPeriod = {
  start: Date;
  end: Date;
};

export type UTCWeekPeriod = {
  start: UTCDate;
  end: UTCDate;
};

export const getWeekPeriod = (date: Date): WeekPeriod => {
  return {
    start: startOfWeek(date, { weekStartsOn: 1 }),
    end: endOfWeek(date, { weekStartsOn: 1 }),
  };
};

export const getUTCWeekPeriod = (date: UTCDate): UTCWeekPeriod => {
  return {
    start: startOfWeek(date, { weekStartsOn: 1 }),
    end: endOfWeek(date, { weekStartsOn: 1 }),
  };
};

export const formatWeekPeriod = (start: Date, end: Date) => {
  return `${formatDateAndMonth(start)} - ${formatDateAndMonth(end)}`;
};

export const localToUTCDate = (date: Date) => {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds(),
    ),
  );
};

export const UTCToLocalDate = (date: Date) => {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  );
};

export const toISODateString = (date: Date) => {
  const isoDateTimeString = date.toISOString();
  return isoDateTimeString.split("T")[0];
};

export function diffInSeconds(date1: Date, date2: Date): number {
  return Math.floor((date1.getTime() - date2.getTime()) / 1000);
}

/**
 * Converts a Date object into a relative time string (e.g., "3 days ago")
 * using Intl.RelativeTimeFormat.
 */
export function dateToRelative(date: Date, locale: string = "da-DK"): string {
  const now = new Date();
  const diffInSeconds = Math.floor((date.getTime() - now.getTime()) / 1000);

  // Define thresholds for different units in seconds
  const units: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
    { unit: "year", seconds: 31536000 },
    { unit: "month", seconds: 2592000 },
    { unit: "week", seconds: 604800 },
    { unit: "day", seconds: 86400 },
    { unit: "hour", seconds: 3600 },
    { unit: "minute", seconds: 60 },
    { unit: "second", seconds: 1 },
  ];

  // Find the first unit that is smaller than the absolute difference
  for (const { unit, seconds } of units) {
    if (Math.abs(diffInSeconds) >= seconds || unit === "second") {
      const value = Math.round(diffInSeconds / seconds);

      const rtf = new Intl.RelativeTimeFormat(locale, {
        numeric: "auto", // Use 'auto' to get "yesterday" instead of "1 day ago"
      });

      return rtf.format(value, unit);
    }
  }

  return ""; // Fallback
}
